import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { TravelTwoIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getTrip } from '../../api/trips/get-trip'
import { updateTrip } from '../../api/trips/update-trip'
import DateChip from '../../components/elements/date-chip'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import TripForm from '../../components/pages/trips/trip-form'
import TripGroupingAssignmentDataTable from '../../components/pages/trips/trip-grouping-assignment-data-table'
import TripPriceDataTable from '../../components/pages/trips/trip-price-data-table'
import { Paths } from '../../lib/constants/path'
import { TripProperties } from '../../lib/types/models/trip/properties.trip'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import NotFoundPage from '../common/not-found'
import { mapTripInputValues } from './utils'

const UpdateTripPage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  if (!id || isNaN(parseInt(id))) return <ItemNotFound title="Identifiant de voyage invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateTrip,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['trip', id] })
      toast.success('Voyage modifié avec succès')
    }
  })

  const { data: trip, isLoading: loadingTrip } = useQuery({
    queryKey: ['trip', id],
    queryFn: () => getTrip(+id)
  })

  if (loadingTrip) return <LoadingScreen />
  if (!trip) return <NotFoundPage />

  const { id: tripId, ...tripData } = trip

  return (
    <div>
      <UserNavBar
        title={`Modifier le voyage ${TripProperties.getTripLabel(trip)}`}
        icon={TravelTwoIcon}
      />
      <div className={'h-4'} />
      <Box>
        <div className="flex items-center gap-3">
          <DateChip date={trip.createdAt} label="Créé" />
          <DateChip date={trip.updatedAt} label="Mise à jour" />
        </div>
        <div className={'h-4'} />
        <TripForm
          tripId={tripId}
          defaultValues={{
            ...tripData,
            arrival: trip.arrival ? getTranslatedName(trip.arrival) : undefined,
            departure: trip.departure ? getTranslatedName(trip.departure) : undefined,
            journeyPlaceId: trip.journeyPlaceId,
            countries: trip.countries.map((country) => country.id)
          }}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.TRIPS_LIST),
            onUpdate: (values) => mutateAsync({ id: trip.id, ...mapTripInputValues(values) }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: trip.id, ...mapTripInputValues(values) })
              navigate(Paths.TRIP_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: trip.id, ...mapTripInputValues(values) })
              back(Paths.TRIPS_LIST)
            }
          })}
        />
      </Box>
      <div className={'h-4'} />
      <TripPriceDataTable tripId={trip.id} />
      <div className={'h-4'} />
      <TripGroupingAssignmentDataTable tripId={trip.id} />
    </div>
  )
}

export default UpdateTripPage
